/* eslint-disable jsx-a11y/label-has-associated-control */
/* eslint-disable no-new */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useGoogleMaps } from "react-hook-google-maps";

// region Libraries
import $ from "jquery";
import _ from "lodash";
// endregion Libraries
// region Constants
import { AlertTypes } from "@shared/constants/alert-types.enum";
// endregion Constants
// region Interfaces
import { CommandPointer, MarkerOptions, PolylineOptions } from "@shared/interfaces/map.interface";
import { TravelAnalytics } from "@shared/interfaces/travel-analytics.interface";
// endregion Interfaces
// region Languages
import useTranslation from "src/translations/useTranslation";
import { GlobalMessages, MapMessages } from "@shared/languages/interfaces";
// endregion Languages
/* region Services */
import api from "@services/api";
import { getAddressByCoordinates } from "@services/googleMaps/addressByCoordinates";
import { matchPathOnPlatform } from "@services/routing/platformRouting";
/* endregion Services */
/* region Hooks */
import { useAuth } from "@hooks/useAuth";
import { useGoogleMapUsageLog } from "@hooks/useGoogleApiUsageLog";
import { useMapFullscreen } from "@hooks/useMapFullscreen";
/* endregion Hooks */
// region Utils
import mapDefaultParameters, { MAPS_API_VERSION } from "@utils/parameters";
import { GoogleApiRequestTypes } from "@shared/constants/google-api-request-types.enum";
import utils from "../../utils/useful-functions";
import { computeDistanceBetween, LatLng } from "@utils/geometry";
// endregion Utils
// region Components
import { MapAllVehiclesCoords, MarkersVisualizationTypes } from "./AllVehiclesCoords";
// endregion Components
// region Styles
import { ContainerMapFixedPointCoord } from "./styles";
// endregion Styles

interface Coord { lat: number, lng: number }

/** Build the first line of an info window — a colored dot, the title and an optional tag — which replaces the
 * window's header row: the close button is hidden, since clicking anywhere on the map closes it
 * @param title What the point is (state, alert, event or route point)
 * @param color Dot color
 * @param tag Short kind label, e.g. "Alerta"
 * @return The markup of the title line
 */
const buildInfoTitle = (title: string, color?: string, tag?: string): string => {
  const header = document.createElement("div");

  header.style.cssText = "display:flex;align-items:center;gap:8px;margin-bottom:8px;padding-top:2px;"
    + "font:500 14px Roboto,Helvetica,Arial,sans-serif;color:#212121;";

  if (color) {
    const dot = document.createElement("span");

    dot.style.cssText = `width:10px;height:10px;border-radius:50%;flex:0 0 auto;background:${color};`;
    header.appendChild(dot);
  }

  const text = document.createElement("span");

  text.textContent = title;
  header.appendChild(text);

  if (tag) {
    const pill = document.createElement("span");

    pill.textContent = tag;
    pill.style.cssText = "font-size:11px;font-weight:500;padding:1px 8px;border-radius:999px;background:#EEEEEE;color:#616161;";
    header.appendChild(pill);
  }

  return header.outerHTML;
};

/** Point of a path closest to a position (vertex search; snapped paths are dense)
 * @param position Position to move onto the path
 * @param path Path points
 * @return The closest path point, or undefined for an empty path
 */
const closestPathPoint = (position: google.maps.LatLngLiteral, path: google.maps.LatLngLiteral[]) => {
  let best: google.maps.LatLngLiteral | undefined;
  let bestDistance = Infinity;
  const origin = new LatLng(position.lat, position.lng);

  path.forEach((point) => {
    const distance = computeDistanceBetween(origin, new LatLng(point.lat, point.lng));

    if (distance < bestDistance) {
      bestDistance = distance;
      best = point;
    }
  });

  return best;
};

type MapFixedPointCoordProps = {
  mapHeight: number,
  latitude: number,
  longitude: number,
  tooltip?: { title: string, content?: string },
  alertMarker?: boolean,
  customMarker?: { url: string },
  customControl?: { element: any, position: google.maps.ControlPosition, action: () => void }
  returnAddressData?: (addressData: google.maps.GeocoderResult) => void,
  dontGetAddress?: boolean
}
const MapFixedPointCoord = React.memo<MapFixedPointCoordProps>((
  { mapHeight, latitude, longitude, tooltip, alertMarker, customMarker, customControl, returnAddressData, dontGetAddress }
) => {

  const { t } = useTranslation();
  const { user } = useAuth();

  const GoogleApiKey = localStorage.getItem("@Fleet:google");
  const { ref, map } = useGoogleMaps(
    `${GoogleApiKey}&libraries=places,drawing&language=pt-BR&v=${MAPS_API_VERSION}`,
    {
      center: mapDefaultParameters.center,
      zoom: 15
    }
  );

  useGoogleMapUsageLog(map, GoogleApiRequestTypes.FIXED_POINT_MAP_LOAD);
  useMapFullscreen(map);

  /* eslint-disable */
  const [stateMarker, setStateMarker] = useState<google.maps.Marker | null>(null);
  const [stateInfoWindow, setStateInfoWindow] = useState<google.maps.InfoWindow | null>(null);
  /* eslint-enable */

  const [control, setControl] = useState<HTMLDivElement>();

  /** Find address according coords and set info in state
   * @param latLng Coords to search address
   */
  const getAddress = useCallback(async (latLng) => {

    if (dontGetAddress) return null;

    // The platform's own geocoder first, Google only when it has no answer
    const address = await getAddressByCoordinates(latLng, { name: user.name, email: user.email });

    if (!address) return t(MapMessages.errorFetchingAddressData);

    if (returnAddressData) returnAddressData(address);

    return address.formatted_address;

  }, [returnAddressData, dontGetAddress, t, user.name, user.email]);

  /** Draw map with point in coords passed in props
   */
  const drawPoint = useCallback(async () => {

    const location: google.maps.LatLng = new google.maps.LatLng({ lat: latitude, lng: longitude });
    const infoWindow: google.maps.InfoWindow = new google.maps.InfoWindow();

    let infoWindowContent;

    // If not pass a address data to show in info window,
    // we search the data with coords to show
    if (!tooltip) infoWindowContent = await getAddress(location);
    else infoWindowContent = `<b>${tooltip.title}</b><br />${tooltip.content || await getAddress(location)}`;

    // Set marker on map
    const marker = new google.maps.Marker({ position: location, map, animation: google.maps.Animation.BOUNCE });

    // Center marker on map
    map.setCenter(location);

    // Verify what marker have to put (Custom, Alert or Default)
    if (customMarker) {
      marker.setIcon({
        url: customMarker.url,
        scaledSize: new google.maps.Size(40, 40)
      });
    } else if (alertMarker) {
      marker.setIcon({
        // eslint-disable-next-line max-len
        url: "data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 56.652 80.932'%3e%3cg id='Grupo_1305' data-name='Grupo 1305' transform='translate(-670.037 -2412)'%3e%3cpath id='Caminho_914' fill='red' data-name='Caminho 914' d='M478.422,480.607A28.306,28.306,0,0,0,450.1,508.934c0,21.244,28.327,52.605,28.327,52.605s28.325-31.361,28.325-52.605A28.306,28.306,0,0,0,478.422,480.607Z' transform='translate(219.942 1931.393)'/%3e%3cpath id='Caminho_856' data-name='Caminho 856' d='M473.006,510.874h3.759v3.759h-3.759Zm0-15.037h3.759v11.278h-3.759Zm1.864-9.4a18.8,18.8,0,1,0,.033,0Zm.019,33.836a15.037,15.037,0,1,1,15.034-15.04v0a15.034,15.034,0,0,1-15.031,15.037h0Z' transform='translate(223.479 1934.834)' fill='%23fff'/%3e%3c/g%3e%3c/svg%3e",
        scaledSize: new google.maps.Size(40, 40)
      });
    }

    // Open info window in marker
    infoWindow.setContent(infoWindowContent);
    if (infoWindowContent) infoWindow.open(map, marker);

    setStateMarker(marker);
    setStateInfoWindow(infoWindow);

    // REVIEW Make component element generic according parameter 'customControl.element'
    // If have a custom control inside map, configure according prop and add your action
    if (customControl && !control) {

      const controlDiv = document.createElement("div");
      const controlUI = document.createElement("div");
      const controlIcon = document.createElement("div");

      controlUI.style.background = "none rgb(255, 255, 255)";
      controlUI.style.border = "2px solid #fff";
      controlUI.style.borderRadius = "2px";
      controlUI.style.boxShadow = "rgb(0 0 0 / 30%) 0px 1px 4px -1px";
      controlUI.style.cursor = "pointer";
      controlUI.style.margin = "10px";
      controlUI.title = t(MapMessages.updateVehicleCurrentPosition);
      controlDiv.appendChild(controlUI);

      // Set CSS for the control interior.
      // eslint-disable-next-line max-len
      controlIcon.innerHTML = "<svg width='24' height='24' xmlns='http://www.w3.org/2000/svg' fill-rule='evenodd' clip-rule='evenodd'><path d='M7 9h-7v-7h1v5.2c1.853-4.237 6.083-7.2 11-7.2 6.623 0 12 5.377 12 12s-5.377 12-12 12c-6.286 0-11.45-4.844-11.959-11h1.004c.506 5.603 5.221 10 10.955 10 6.071 0 11-4.929 11-11s-4.929-11-11-11c-4.66 0-8.647 2.904-10.249 7h5.249v1z'/></svg>";
      controlIcon.style.display = "flex";
      controlIcon.style.padding = "5px";
      controlUI.appendChild(controlIcon);

      // Add action on control and put inside map
      controlDiv.addEventListener("click", () => customControl.action());
      map.controls[customControl.position].push(controlDiv);

      setControl(controlDiv as HTMLDivElement);
    }

  }, [getAddress, latitude, longitude, map, tooltip, alertMarker, customMarker,
    customControl, control, setStateInfoWindow, setStateMarker, t
  ]);

  useEffect(() => {

    // When map is render, draw according point
    if (map) drawPoint().then();

    // Clear map of previous markers and info windows
    return () => {

      setStateInfoWindow(null);
      setStateMarker((state) => {
        if (state) state.setMap(null);

        return null;
      });
    };

  }, // drawPoint function duplicates request to Google API if it's present in dependency array
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [map, latitude, longitude, setStateMarker, setStateInfoWindow]);

  return (
    <ContainerMapFixedPointCoord ref={ref} style={{ width: "100%", height: mapHeight }} />
  );
});

type MapAnalyticsProps = {
  mapHeight: number | string,
  polygons?: Omit<google.maps.PolygonOptions, "map">[],
  markers?: MarkerOptions[],
  polylines?: PolylineOptions[],
  circles?: Omit<google.maps.CircleOptions, "map">[],
  polygonPlaces?: Omit<google.maps.PolygonOptions, "map">[],
  commandsPointer?: CommandPointer,
  filter?: {class: string, status: boolean}[],
  snapToRoads?: boolean,
  /** Marker classes moved onto the road-snapped line of the same class (route points follow the drawn route) */
  snapMarkerClasses?: string[],
  /** Marker classes thinned by zoom: one is hidden when it would sit too close (in pixels) to one already shown */
  declutterMarkerClasses?: string[],
  /** Opens a marker's card and centers the map on it (a new nonce reopens the same marker) */
  focusMarker?: { index: number, nonce: number },
  /** Route replay: the lines of `classes` are faded and redrawn in their color up to point `pathIndex` of line `leg`
   * (plus `fraction` of the way to the next one), with a marker on that spot (kept at the center with `follow`);
   * null shows the route as usual */
  replay?: { classes: string[], leg: string, pathIndex: number, fraction: number, follow?: boolean } | null,
  /** Right click on the map, on a line or on a marker: where it was (lat/lng) and where on screen (client pixels) */
  onContextMenu?: (event: { position: google.maps.LatLngLiteral, clientX: number, clientY: number }) => void,
  /** Full screen button on the map (shown unless false) */
  fullscreenControl?: boolean,
}

// Minimum distance, in screen pixels, between two decluttered markers (and between them and any other marker)
const MIN_MARKER_SPACING_PX = 34;
// A marker focused from outside the map is shown at least this close
const FOCUS_MIN_ZOOM = 17;
// Route still ahead during a replay
const REPLAY_FADED_COLOR = "#9E9E9E";

type SnappedPolyline = PolylineOptions & { snappedIndex?: number[] };
const MapAnalytics: React.FC<MapAnalyticsProps> = (
  {
    mapHeight, polygons, markers, polylines, circles, polygonPlaces, commandsPointer, filter, snapToRoads, snapMarkerClasses,
    declutterMarkerClasses, focusMarker, replay, onContextMenu, fullscreenControl
  }
) => {

  interface StatePolyline extends google.maps.Polyline {
    class?: string;
  }
  const markersCount = markers ? markers.length : 0;
  const GoogleApiKey = localStorage.getItem("@Fleet:google");
  const [stateMarkers, setStateMarkers] = useState<google.maps.Marker[]>([]);
  const [statePolylines, setStatePolylines] = useState<StatePolyline[]>([]);
  const [stateInfoWindows, setStateInfoWindows] = useState<google.maps.InfoWindow[]>([]);
  const [stateLastOpenedWindow, setStateLastOpenedWindow] = useState<number | null>(null);

  // Where each recorded point of a line landed on its road-snapped version, per line class (used by the replay)
  const snappedIndexRef = useRef<Record<string, number[]>>({});
  const [snapVersion, setSnapVersion] = useState(0);
  // Replay drawing: the colored part of each line and the moving marker
  const replayLinesRef = useRef<Record<string, google.maps.Polyline>>({});
  const replayMarkerRef = useRef<google.maps.Marker | null>(null);
  // Lines faded by the replay, with the look they had, to give it back when the replay ends
  const fadedLinesRef = useRef(new Map<google.maps.Polyline, { strokeColor?: string, strokeOpacity?: number }>());
  // Snapping the same line again (the map redraws when, e.g., the ZMRC zones arrive) reuses the first answer
  const snapCacheRef = useRef(new Map<string, Promise<SnappedPolyline>>());

  // Right clicks reach the current handler through a ref, so listeners bound once never go stale
  const contextMenuRef = useRef(onContextMenu);

  contextMenuRef.current = onContextMenu;
  const emitContextMenu = useCallback((event: google.maps.MapMouseEvent) => {
    const mouse = event.domEvent as MouseEvent | undefined;

    if (!event.latLng || !mouse || !contextMenuRef.current) return;
    contextMenuRef.current({ position: event.latLng.toJSON(), clientX: mouse.clientX, clientY: mouse.clientY });
  }, []);

  /* eslint-disable */
  const [statePolygons, setStatePolygons] = useState<google.maps.Polygon[]>([]);
  const [stateCircles, setStateCircles] = useState<google.maps.Circle[]>([]);
  const [statePolygonPlaces, setStatePolygonPlaces] = useState<google.maps.Polygon[]>([]);
  /* eslint-enable */

  const { user } = useAuth();

  const { ref, map } = useGoogleMaps(
    `${GoogleApiKey}&libraries=places,drawing&language=pt-BR&v=${MAPS_API_VERSION}`,
    {
      center: mapDefaultParameters.center,
      mapTypeId: "roadmap",
      zoom: 18
    }
  );

  useGoogleMapUsageLog(map, GoogleApiRequestTypes.ANALYTICS_MAP_LOAD);
  // A page that draws its own full screen (with more than the map in it) turns the map's button off
  useMapFullscreen(map, fullscreenControl !== false);

  // Reduce elements on map filters to one Object {...obj, [class]: status } and add last_position permanent visible
  const mapFilter = useMemo(() => {

    if (filter) {
      return filter.reduce((acc, cv) => ({ ...acc, [cv.class]: cv.status }), { "last_position": true, "area_label": true });
    }

    return {};
  }, [filter]);

  /** Pin a marker in map
   * @param marker Marker to be pinned in the map
   */
  const placeMarker = useCallback((marker: MarkerOptions, id: number): google.maps.Marker | undefined => {

    if (!_.isEmpty(marker)) {

      const instancedMarker = new google.maps.Marker({ ...marker, map, optimized: markersCount > 256 ? false : undefined });
      // Instance the info window component for each marker. A titled marker shows its title as the first line and
      // has no header row with a close button: clicking anywhere on the map closes it
      const infoWindow = new google.maps.InfoWindow((marker.infoTitle ? { headerDisabled: true } : {}) as google.maps.InfoWindowOptions);
      const title = marker.infoTitle ? buildInfoTitle(marker.infoTitle, marker.infoColor, marker.infoTag) : "";

      // Set InfoWindow content
      infoWindow.setContent(`<div id="marker-info-${id}">${title}${marker.infoContent}</div>`);

      // Create event click in marker with his info — close all others first
      google.maps.event.addListener(instancedMarker, "click", ((instancedMarker, infoWindow) => () => {
        setStateInfoWindows((current) => {
          current.forEach((iw) => iw.close());

          return current;
        });
        infoWindow.open(map, instancedMarker);
      })(instancedMarker, infoWindow));

      google.maps.event.addListener(instancedMarker, "rightclick", emitContextMenu);

      // Save in states to manipulate later
      setStateInfoWindows((res) => [...res, infoWindow]);
      setStateMarkers((res) => [...res, instancedMarker]);

      return instancedMarker;
    }

    return undefined;

  }, [map, setStateMarkers, setStateInfoWindows, emitContextMenu]);

  /** Draw a polyline in map
   * @param polyline Polyline to be drawn in the map
   */
  const placePolyline = useCallback((polyline: google.maps.PolylineOptions) => {

    if (!_.isEmpty(polyline)) {

      const instancedPolyline = new google.maps.Polyline(polyline);

      instancedPolyline.setMap(map);
      google.maps.event.addListener(instancedPolyline, "rightclick", emitContextMenu);
      setStatePolylines((res) => [...res, instancedPolyline]);
    }

  }, [map, emitContextMenu]);

  /** Place a Polygon in map
   * @param polygon Polygon to be placed in the map
   */
  const placePolygon = useCallback((polygon: google.maps.PolygonOptions) => {

    if (!_.isEmpty(polygon)) {

      const instancedPolygon = new google.maps.Polygon({
        ...polygon,
        map
      });

      setStatePolygons((state) => [...state, instancedPolygon]);
    }

  }, [map]);

  /** Place a Circle in map
   * @param circle Circle to be placed in the map
   */
  const placeCircle = useCallback((circle: google.maps.CircleOptions) => {

    if (!_.isEmpty(circle)) {

      // TODO Save circle in a state
      const instancedCircle = new google.maps.Circle({
        ...circle,
        map
      });

      setStateCircles((state) => [...state, instancedCircle]);
    }

  }, [map]);

  /** Place a place in polygon format in map
   * @param polygon Polygon to be placed in the map
   */
  const placePolygonPlace = useCallback((polygon: google.maps.PolygonOptions) => {

    if (!_.isEmpty(polygon)) {

      const instancedPolygonPlace = new google.maps.Polygon({
        ...polygon,
        map
      });

      setStatePolygonPlaces((state) => [...state, instancedPolygonPlace]);
    }

  }, [map]);

  // Remove markers from map
  const clearMarkers = useCallback(() => {
    setStateInfoWindows([]);
    setStateMarkers((state) => state.map((mark) => {
      mark.setMap(null);

      return mark;
    }).filter((f) => false));
  }, [setStateMarkers]);

  // Remove polylines from map
  const clearPolylines = useCallback(() => {
    setStatePolylines((state) => state.map((polyline) => {
      polyline.setMap(null);

      return polyline;
    }).filter((f) => false));
  }, [setStatePolylines]);

  // Remove markers from map
  const clearCircles = useCallback(() => {
    setStateCircles((state) => state.map((circle) => {
      circle.setMap(null);

      return circle;
    }).filter((f) => false));
  }, [setStateCircles]);

  // Remove markers from map
  const clearPolygons = useCallback(() => {
    setStatePolygons((state) => state.map((polygon) => {
      polygon.setMap(null);

      return polygon;
    }).filter((f) => false));
  }, [setStatePolygons]);

  // Remove markers from map
  const clearPolygonPlaces = useCallback(() => {
    setStatePolygonPlaces((state) => state.map((polygon) => {
      polygon.setMap(null);

      return polygon;
    }).filter((f) => false));
  }, [setStatePolygonPlaces]);

  // Set path going routes and coming routes (if have)
  const getSnappedCoord = useCallback(async (polyline: PolylineOptions) => {

    if (polyline.class === "zmrc") return polyline;

    // Snap to roads
    const path = (polyline.path ?? []) as google.maps.LatLngLiteral[];
    const pathMain: any[] = [];
    let pathValues: any[] = [];

    // Mount the array of polyline points
    path.forEach((point, i) => {
      pathValues.push(`${point.lat.toFixed(6)},${point.lng.toFixed(6)}`);

      // We separated the data into groups of 100 in 100
      // (Google API roads can request a maximum of 100 polylines at a time)
      if (pathValues.length === 100 || i === path.length - 1) {
        pathMain.push(pathValues);
        pathValues = [];
      }
    });

    const snapCoordToRoadsByGoogle = async (curPath: any[]) => new Promise((resolve, reject) => {
      $.ajax({
        url: "https://roads.googleapis.com/v1/snapToRoads",
        type: "GET",
        data: {
          interpolate: true,
          key: GoogleApiKey,
          path: curPath.join("|")
        },
        success(data) {
          const snappedCoordinates: Coord[] = [];
          // Which recorded point each snapped point stands for (the road points added between them have none)
          const originalIndexes: (number | undefined)[] = [];

          if (!$.isEmptyObject(data)) {
            for (const snappedPoint of data.snappedPoints) {
              const latlng = {
                lat: snappedPoint.location.latitude,
                lng: snappedPoint.location.longitude
              };

              snappedCoordinates.push(latlng);
              originalIndexes.push(snappedPoint.originalIndex);
            }
          }
          resolve({ coords: snappedCoordinates, originalIndexes });
        },
        error(error) {
          reject(error);
        }
      });
    }).finally(async () => {
      try {
        await api.post("/logs/save", {
          context: "Frontend",
          description: GoogleApiRequestTypes.SNAP_COORDS_TO_ROADS,
          user: {
            name: user.name,
            email: user.email
          }
        });
      } catch (error) {
        console.log(error);
      }
    });

    // The platform's own route service (OSRM) first, Google Roads only when it cannot answer
    const snapCoordToRoads = async (curPath: string[]) => {
      const stretch = await matchPathOnPlatform(curPath.map((point) => {
        const [lat, lng] = point.split(",").map(Number);

        return { lat, lng };
      }));

      return stretch ?? snapCoordToRoadsByGoogle(curPath);
    };

    // Get coords according polylines (With road points)
    const promises = pathMain.map((curPath) => snapCoordToRoads(curPath));
    const groupedPath = (await Promise.all(promises)) as { coords: Coord[], originalIndexes: (number | undefined)[] }[];

    // Where each recorded point landed on the snapped line (the API answers per group of 100 points); a point the
    // API dropped keeps the position of the one before it
    const snappedIndex: number[] = new Array(path.length).fill(-1);
    let offset = 0;

    groupedPath.forEach((group, groupIndex) => {
      group.originalIndexes.forEach((original, i) => {
        if (original !== undefined && snappedIndex[groupIndex * 100 + original] === -1) snappedIndex[groupIndex * 100 + original] = offset + i;
      });
      offset += group.coords.length;
    });
    for (let i = 0; i < snappedIndex.length; i++) if (snappedIndex[i] === -1) snappedIndex[i] = i ? snappedIndex[i - 1] : 0;

    return {
      ...polyline,
      path: groupedPath.reduce((acc, cv) => [...acc, ...cv.coords], [] as Coord[]),
      snappedIndex
    } as SnappedPolyline;
  }, [GoogleApiKey, user.name, user.email]);

  const getSnappedCoordCached = useCallback((polyline: PolylineOptions): Promise<SnappedPolyline> => {
    const path = (polyline.path ?? []) as google.maps.LatLngLiteral[];
    const key = `${polyline.class}|${path.length}|${JSON.stringify(path[0])}|${JSON.stringify(path[path.length - 1])}`;
    let pending = snapCacheRef.current.get(key);

    if (!pending) {
      pending = getSnappedCoord(polyline);
      snapCacheRef.current.set(key, pending);
    }

    return pending;
  }, [getSnappedCoord]);

  // Init map, places Markers, Polygons and Circles
  useEffect(() => {

    // Init map
    if (map) {

      // Object to fit map according markers position
      const bounds = new google.maps.LatLngBounds();

      // Place Markers on map
      const placedMarkers: { options: MarkerOptions, instance?: google.maps.Marker }[] = [];

      if (markers) {

        markers.forEach((marker, idx) => {
          placedMarkers.push({ options: marker, instance: placeMarker(marker, idx) });
          bounds.extend(marker.position as google.maps.LatLng);
        });
      }

      // Draw polylines on map
      if (polylines) {
        if (snapToRoads) {
          // Snap polyline to roads, then move the route markers onto the road of their own leg
          // a leg the Roads API cannot snap is drawn as recorded, without holding the others back
          Promise.all(Object.keys(polylines).map((key) => getSnappedCoordCached(polylines[key]).catch(() => polylines[key] as SnappedPolyline)))
            .then((snappedPolylines) => {
              snappedPolylines.forEach((polyline) => {
                const { snappedIndex, ...options } = polyline;

                if (options.class) {
                  if (snappedIndex) snappedIndexRef.current[options.class] = snappedIndex;
                  else delete snappedIndexRef.current[options.class];
                }
                placePolyline(options);
              });
              setSnapVersion((version) => version + 1);

              if (snapMarkerClasses?.length) {
                placedMarkers.forEach(({ options, instance }) => {
                  if (!instance || !options.class || !snapMarkerClasses.includes(options.class)) return;

                  const leg = snappedPolylines.find((polyline) => polyline.class === options.class);
                  const point = leg && closestPathPoint(
                  options.position as google.maps.LatLngLiteral, (leg.path ?? []) as google.maps.LatLngLiteral[]
                  );

                  if (point) instance.setPosition(point);
                });

                applyVisibilityRef.current();
              }
            }).catch((error) => console.log(error));
        } else {
          Object.keys(polylines).forEach((key) => {
            placePolyline(polylines[key]);
          });
        }
      }

      // Place Polygons on map
      if (polygons) {
        polygons.forEach((arrayPolygon) => {
          for (let i = 0; i < (arrayPolygon as any).length; i++) {
            const polygon = arrayPolygon[i];

            placePolygon(polygon);
          }
        });
      }

      // Place Circles on map
      if (circles) {
        circles.forEach((circle) => {
          placeCircle(circle);
          bounds.extend(circle.center as google.maps.LatLng);
        });
      }

      // Place Polygon Places on map
      if (polygonPlaces) {
        polygonPlaces.forEach((polygon) => {
          placePolygonPlace(polygon);

          // A location defined by a drawn area has no circle to be framed by, so extend the bounds
          // with its own vertices — otherwise it can end up outside the visible map
          if (Array.isArray(polygon.paths)) {
            (polygon.paths as google.maps.LatLngLiteral[]).forEach((path) => bounds.extend(path));
          }
        });
      }

      // Always fit map based on Markers and Circles
      map.fitBounds(bounds);
    }

    return () => {
      clearMarkers();
      clearPolylines();
      clearCircles();
      clearPolygons();
      clearPolygonPlaces();
    };

  }, [map, polygons, markers, polylines, circles, polygonPlaces, snapToRoads, snapMarkerClasses, placeCircle, placeMarker, placePolyline,
    placePolygon, placePolygonPlace, clearMarkers, clearPolylines, clearCircles, clearPolygons, clearPolygonPlaces, getSnappedCoordCached]);

  // Right click on the map itself
  useEffect(() => {
    if (!map) return undefined;

    const listener = google.maps.event.addListener(map, "rightclick", emitContextMenu);

    return () => google.maps.event.removeListener(listener);
  }, [map, emitContextMenu]);

  // Close all InfoWindows when clicking on the map background
  useEffect(() => {
    if (!map) return;

    const listener = google.maps.event.addListener(map, "click", () => {
      setStateInfoWindows((current) => {
        current.forEach((iw) => iw.close());

        return current;
      });
    });

    return () => google.maps.event.removeListener(listener);
  }, [map]);

  // Control click and hover markers
  useEffect(() => {

    // Variable to backup icon
    let icon: google.maps.Icon;

    if (map) {

      // Function to handle when item is clicked in list
      const handleSelected = () => {

        if (commandsPointer?.selected !== undefined && commandsPointer?.selected !== null) {

          // Don't open info windows if already opened (prevent multiples windows in render)
          if (stateLastOpenedWindow !== commandsPointer?.selected) {

            setStateLastOpenedWindow(commandsPointer?.selected);
            const instancedMarker = stateMarkers[commandsPointer?.selected];

            stateInfoWindows[commandsPointer?.selected].open(map, instancedMarker);
          }
        }
      };

      // Function to handle when item is hovered in list
      const handleHovered = () => {
        if (commandsPointer?.hovered !== undefined && commandsPointer?.hovered !== null && stateMarkers[commandsPointer.hovered]) {
          const size = { width: 38, height: 76 } as google.maps.Size;

          // Save icon to restore after hover
          icon = stateMarkers[commandsPointer.hovered].getIcon() as google.maps.Icon;
          // Set icon increased size
          stateMarkers[commandsPointer.hovered].setIcon({ ...icon, size, scaledSize: size });

        }
      };

      // Prevent execute handlers before markers init
      if (markers && markers.length > 0) {

        // If hovered and selected is the same, execute Selected Handler
        if (!_.isNull(commandsPointer?.selected)) handleSelected();
        else if (!_.isNull(commandsPointer?.hovered)) handleHovered();
      }
    }

    return () => {
      // If icon expanded, reset to original size
      if (icon) {
        // Check if markers are instanced
        if (markers && markers.length > 0 && commandsPointer?.hovered !== undefined && commandsPointer?.hovered !== null) {
          if (stateMarkers[commandsPointer.hovered]) {
            stateMarkers[commandsPointer.hovered].setIcon(icon);
          }
        }
      }
    };
  }, [commandsPointer, markers, map, stateInfoWindows, stateLastOpenedWindow, stateMarkers]);

  /** Show each marker whose layer is on; decluttered markers (route arrows) are also spaced by the current zoom,
   * in their order, so the route never turns into a pile of overlapping icons — zooming in reveals the rest */
  const applyMarkerVisibility = useCallback(() => {

    if (!markers) return;

    const projection = map?.getProjection();
    const scale = 2 ** (map?.getZoom() ?? 0);
    const toPixel = (marker: google.maps.Marker) => {
      const position = marker.getPosition();
      const world = position && projection?.fromLatLngToPoint(position);

      return world ? { x: world.x * scale, y: world.y * scale } : null;
    };
    const tooClose = (a: { x: number, y: number }, b: { x: number, y: number }) => Math.hypot(a.x - b.x, a.y - b.y) < MIN_MARKER_SPACING_PX;
    const declutter = (index: number) => !!declutterMarkerClasses?.includes(markers[index]?.class || "");

    // Markers that are never thinned take their space first
    const fixed = stateMarkers
      .map((marker, i) => (!declutter(i) && mapFilter[markers[i]?.class || ""] ? toPixel(marker) : null))
      .filter((point): point is { x: number, y: number } => !!point);
    let lastShown: { x: number, y: number } | null = null;

    for (let i = 0; i < stateMarkers.length; i++) {
      let visible = !!mapFilter[markers[i]?.class || ""];

      if (visible && declutter(i) && projection) {
        const point = toPixel(stateMarkers[i]);

        if (point && ((lastShown && tooClose(point, lastShown)) || fixed.some((other) => tooClose(point, other)))) visible = false;
        else if (point) lastShown = point;
      }

      stateMarkers[i].setVisible(visible);
      if (stateMarkers[i]?.getAnimation() != null) {
        stateMarkers[i].setAnimation(stateMarkers[i].getAnimation() || null);
      }
    }
  }, [map, markers, stateMarkers, mapFilter, declutterMarkerClasses]);

  // The snapping callback runs later than this render: it reaches the current version through a ref
  const applyVisibilityRef = useRef(applyMarkerVisibility);

  applyVisibilityRef.current = applyMarkerVisibility;

  // Re-space the decluttered markers whenever the zoom settles
  useEffect(() => {
    if (!map || !declutterMarkerClasses?.length) return undefined;

    const listener = google.maps.event.addListener(map, "idle", () => applyVisibilityRef.current());

    return () => google.maps.event.removeListener(listener);
  }, [map, declutterMarkerClasses]);

  // Open a marker chosen outside the map (e.g. an item of the travel timeline)
  useEffect(() => {
    if (!map || !focusMarker) return;

    const marker = stateMarkers[focusMarker.index];
    const infoWindow = stateInfoWindows[focusMarker.index];
    const position = marker?.getPosition();

    if (!marker || !infoWindow || !position) return;

    stateInfoWindows.forEach((window) => window.close());
    // Zoom in when the map still frames the whole travel (a panTo animation would cancel the zoom: jump instead)
    if ((map.getZoom() ?? 0) < FOCUS_MIN_ZOOM) {
      map.setZoom(FOCUS_MIN_ZOOM);
      map.setCenter(position);
    } else {
      map.panTo(position);
    }
    infoWindow.open(map, marker);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusMarker?.nonce]);

  // Route replay: fade the replayed lines, redraw each one in its color up to the cursor and move the marker there
  useEffect(() => {
    if (!map) return;

    const originalOf = (line: StatePolyline) => polylines?.find((options) => options.class === line.class);

    if (!replay) {
      Object.values(replayLinesRef.current).forEach((line) => line.setMap(null));
      replayLinesRef.current = {};
      replayMarkerRef.current?.setMap(null);
      replayMarkerRef.current = null;
      // Only the lines the replay faded get their look back (maps without replay are never touched)
      fadedLinesRef.current.forEach((look, line) => line.setOptions(look));
      fadedLinesRef.current.clear();

      return;
    }

    const currentLeg = replay.classes.indexOf(replay.leg);
    let cursorPosition: google.maps.LatLngLiteral | undefined;

    statePolylines.forEach((line) => {
      const legOrder = replay.classes.indexOf(line.class ?? "");

      if (legOrder < 0 || !line.class) return;

      const original = originalOf(line);
      const path = line.getPath().getArray().map((point) => point.toJSON());
      let drawn: google.maps.LatLngLiteral[] = [];

      if (legOrder < currentLeg) {
        drawn = path;
      } else if (legOrder === currentLeg && path.length) {
        const index = snappedIndexRef.current[line.class];
        const at = (i: number) => (index ? index[Math.min(i, index.length - 1)] ?? 0 : Math.min(i, path.length - 1));
        const exact = at(replay.pathIndex) + (at(replay.pathIndex + 1) - at(replay.pathIndex)) * replay.fraction;
        const base = Math.min(Math.floor(exact), path.length - 1);
        const from = path[base];
        const to = path[Math.min(base + 1, path.length - 1)];
        const rest = exact - base;

        cursorPosition = { lat: from.lat + (to.lat - from.lat) * rest, lng: from.lng + (to.lng - from.lng) * rest };
        drawn = [...path.slice(0, base + 1), cursorPosition];
      }

      if (!fadedLinesRef.current.has(line)) {
        fadedLinesRef.current.set(line, { strokeColor: line.get("strokeColor"), strokeOpacity: line.get("strokeOpacity") });
      }
      line.setOptions({ strokeColor: REPLAY_FADED_COLOR, strokeOpacity: 0.45 });

      let progress = replayLinesRef.current[line.class];

      if (!progress) {
        progress = new google.maps.Polyline({
          map,
          clickable: false,
          strokeColor: original?.strokeColor,
          strokeOpacity: 0.95,
          strokeWeight: original?.strokeWeight ?? 8,
          icons: original?.icons,
          zIndex: 2
        });
        replayLinesRef.current[line.class] = progress;
      }
      progress.setPath(drawn);
      progress.setVisible(!!mapFilter[line.class]);
    });

    if (cursorPosition) {
      if (!replayMarkerRef.current) {
        replayMarkerRef.current = new google.maps.Marker({
          map,
          clickable: false,
          zIndex: 3000,
          icon: { path: google.maps.SymbolPath.CIRCLE, scale: 8, fillColor: "#212121", fillOpacity: 1, strokeColor: "#FFFFFF", strokeWeight: 3 }
        });
      }
      replayMarkerRef.current.setPosition(cursorPosition);

      // Playing, the map follows the marker at its center; paused, it only brings the marker back into sight
      if (replay.follow) map.setCenter(cursorPosition);
      else if (!map.getBounds()?.contains(cursorPosition)) map.panTo(cursorPosition);
    }
  }, [map, replay, statePolylines, polylines, snapVersion, mapFilter]);

  // Leaving the map takes the replay drawing with it
  useEffect(() => () => {
    Object.values(replayLinesRef.current).forEach((line) => line.setMap(null));
    replayLinesRef.current = {};
    replayMarkerRef.current?.setMap(null);
    replayMarkerRef.current = null;
  }, []);

  // Filter markers and polylines using setVisible()
  useEffect(() => {

    // Verify markers to filter
    applyMarkerVisibility();

    // Verify polylines to filter
    if (polylines) {
      for (const poly of statePolylines) {
        const visible = !!mapFilter[poly?.class || ""];

        poly.setVisible(visible);
      }
    }

    // Verifiy polygons to filter
    if (polygons)
      for (const polygon of statePolygons) {
        const visible = !!mapFilter[polygon["class"] || ""];

        polygon.setVisible(visible);
      }

  }, [filter, mapFilter, markers, polylines, stateMarkers, statePolylines, polygons, applyMarkerVisibility]);

  // Return Google Maps Component
  return (<div className="map" ref={ref} style={{ width: "100%", height: mapHeight }} />);
};

type PopperMarkerProps = {
  index: number;
  isGoing: boolean;
  record: TravelAnalytics;
  /** The info window header already shows what the point is — skip the "Type" row */
  hideTitle?: boolean;
  /** Construction of the travel, to show how far the point is from it */
  destination?: { latitude?: string; longitude?: string };
}

type InfoRow = { title: string; content: string };

/** Body of a map marker info window: one compact list per point, without repeated rows.
 * An alert and the event that raised it are the same occurrence (an improper discharge is stored as both),
 * so they are shown once — the alert, with the event's end and duration.
 */
const PopperMarker = React.memo<PopperMarkerProps>((
  { index, isGoing, record: recordParam, hideTitle = false, destination }
) => {

  const record = recordParam as TravelAnalytics;

  const { t } = useTranslation();

  const fullDate = (date?: string | Date) => `${utils.formatDateIfHave(date, "fullDate")}`;
  const duration = (start?: string | Date, end?: string | Date) => (start && end
    ? `${utils.formatDateIfHave(utils.calcDataRange(new Date(start), new Date(end)), "durationDescriptiveTime")}`
    : "");

  const { alert, event, vehicleState } = record;

  // The event raised by the alert is the same occurrence: merge them instead of listing both
  const alertEvent = alert?.event;
  const eventIsAlert = !!alert && !!event
    && (event.type?.description === alert.type?.description
      || (!!alertEvent?.start_date && new Date(alertEvent.start_date).getTime() === new Date(event.start_date).getTime()));
  const occurrenceEvent = eventIsAlert ? event : alertEvent;

  let description: string | undefined;
  let start: string | Date | undefined;
  let finish: string | Date | undefined;
  let speed: number | undefined = record.content?.SPD?.content !== undefined ? Number(record.content.SPD.content) : undefined;

  if (alert) {
    description = alert.type.description;
    start = alert.occurrence_date;
    finish = occurrenceEvent?.finish_date;

    // Speeding: the relevant speed is the one the event recorded, and it has no duration
    if (AlertTypes.EXCESSO_DE_VELOCIDADE === (alertEvent?.type?.description as AlertTypes)) {
      finish = undefined;
      speed = alertEvent?.speed !== undefined ? Number(alertEvent.speed) : speed;
    }
  } else if (event) {
    description = event.type.description;
    start = event.start_date;
    finish = event.finish_date;
  } else if (vehicleState) {
    description = vehicleState.status.description;
    start = vehicleState.registration_date;
    finish = vehicleState.finish_date;
  } else {
    start = record.occurrence_date;
  }

  // Distance from the point to the travel's construction
  let distance = "";
  const lat = Number(event?.latitude ?? record.content?.LAT?.content);
  const lng = Number(event?.longitude ?? record.content?.LON?.content);

  if (destination?.latitude && destination?.longitude && Number.isFinite(lat) && Number.isFinite(lng)) {
    const meters = computeDistanceBetween(
      new LatLng(lat, lng), new LatLng(Number(destination.latitude), Number(destination.longitude))
    );

    distance = meters >= 1000 ? `${(meters / 1000).toFixed(1).replace(".", ",")} km` : `${Math.round(meters)} m`;
  }

  const rows: InfoRow[] = [];

  if (!hideTitle && description) rows.push({ title: t(GlobalMessages.type), content: description });
  rows.push({ title: t(GlobalMessages.start), content: fullDate(start) });
  if (finish) rows.push({ title: t(GlobalMessages.end), content: fullDate(finish) });
  if (finish) rows.push({ title: t(GlobalMessages.duration), content: duration(start, finish) });
  if (speed !== undefined && Number.isFinite(speed)) {
    rows.push({ title: t(GlobalMessages.speed), content: speed < 1 ? t(MapMessages.stopped) : `${Math.round(speed)} km/h` });
  }
  if (distance) rows.push({ title: t(MapMessages.distanceToConstruction), content: distance });
  rows.push({ title: t(MapMessages.route), content: isGoing ? t(MapMessages.outwardTrip) : t(MapMessages.returnTrip) });

  // An unrelated event at the same instant (rare) is still listed, by name
  const otherEvent = event && !eventIsAlert && alert ? event.type.description : undefined;

  return (
    <div
      id={`marker-info-${index}-content`}
      className="vehicle-travel-details__map-info-content"
      style={{ minWidth: 220, font: "13px Roboto, Helvetica, Arial, sans-serif", color: "#424242" }}
    >
      {rows.map((row) => (
        <div key={row.title} style={{ display: "flex", justifyContent: "space-between", gap: 16, padding: "2px 0" }}>
          <span style={{ color: "#757575" }}>{row.title}</span>
          <span style={{ textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{row.content}</span>
        </div>
      ))}
      {otherEvent && (
        <div style={{ marginTop: 6, paddingTop: 6, borderTop: "1px solid #EEEEEE", color: "#757575" }}>
          {`${t(MapMessages.eventTag)}: ${otherEvent}`}
        </div>
      )}
    </div>
  );
});

export {
  MapFixedPointCoord,
  MapAllVehiclesCoords,
  MapAnalytics,
  PopperMarker
};
export type { MarkersVisualizationTypes };
